/**
 * TrpcMemberProfileReader — the production `MemberProfilePort` adapter over the
 * typed tRPC client (034 US2, T025). Calls the live `member.profile.get`
 * procedure: the member's cookie session authenticates the same-origin request,
 * and `memberProcedure` on the API side guarantees only a member session reads
 * it, scoped to the JWT `sub` (never a client-supplied id).
 *
 * Both `member` and `member.profile` are OPTIONAL on the published `AppRouter`
 * (the conditional-spread posture — `member.profile` is mounted only when the IS
 * client is configured). The adapter narrows before calling and fails loudly
 * (a `{ status: 'unavailable' }` result or an absent namespace throws, caught by
 * `PortErrorBoundary` → the "temporarily unavailable" state) rather than
 * rendering fabricated fields — an absent namespace is a composition/config
 * error, not "no profile" (P7).
 */

import type { WebTrpcClient } from '../trpc/client.js';
import type {
  ContactDetailsPatch,
  MemberProfile,
  MemberProfilePort,
  MemberWriteOutcome,
} from './MemberProfilePort.js';

export class TrpcMemberProfileReader implements MemberProfilePort {
  readonly #client: WebTrpcClient;

  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  async getProfile(): Promise<MemberProfile> {
    const member = this.#client.member;
    if (member?.profile === undefined) {
      throw new Error(
        'TrpcMemberProfileReader: member.profile is absent on the AppRouter — the ' +
          'profile surface requires the IntegrationService client to be configured (034 US2).',
      );
    }
    const result = await member.profile.get.query();
    if (result.status === 'unavailable') {
      // Honest temporarily-unavailable — never a fabricated or cross-member
      // profile (FR-008/FR-011). PortErrorBoundary renders the unavailable state.
      throw new Error('member.profile.get: temporarily unavailable');
    }
    return result.profile;
  }

  async updateContact(
    patch: ContactDetailsPatch,
    idempotencyKey: string,
  ): Promise<MemberWriteOutcome> {
    const member = this.#client.member;
    if (member?.profile === undefined) {
      throw new Error(
        'TrpcMemberProfileReader: member.profile is absent on the AppRouter — the ' +
          'profile write requires the IntegrationService client to be configured (035 US1).',
      );
    }
    // The API returns the same honest discriminated shape (ok/validation/
    // conflict/unavailable); forward it verbatim. A thrown transport error is
    // caught by `useMemberProfileWriter` and surfaced as "couldn't save".
    return member.profile.update.mutate({ patch, idempotencyKey });
  }
}
